import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

// Production error reporting.
//
// Before this existed there were ~50 console.error calls and nothing
// collecting them: if the app broke for a real user, we found out only if
// they bothered to email. This ships errors to a Cloud Function that logs
// them where GCP Error Reporting can group them.
//
// Deliberately NOT a third-party service (Sentry et al). Two reasons:
// adding one means a new subprocessor to disclose in the privacy policy and
// re-review with counsel, and it means user data leaving Google's estate.
// Cloud Logging is already in the trust boundary the app runs inside.
//
// WHAT IS SENT (and nothing else -- same rule as analytics in App.js):
//   message, stack, the ROUTE pattern, browser UA, viewport, release.
// WHAT IS NEVER SENT:
//   uid, email, recipe/comment text, search terms, allergy or health data.
// An error's *message* can technically contain anything a thrown Error was
// given, so scrubMessage below strips the identifiers we know can appear.

const MAX_PER_SESSION = 10;   // hard ceiling: an error loop can't run up cost
const MAX_MESSAGE = 500;
const MAX_STACK = 3000;
const DEDUPE_WINDOW_MS = 60 * 1000;

let sentCount = 0;
const recentlySent = new Map(); // fingerprint -> timestamp

// Route patterns, so a report says "/recipe/:id" rather than naming the
// recipe someone was reading. Mirrors the page_path rule for analytics.
const ROUTE_PATTERNS = [
  [/^\/recipe\/[^/]+\/edit$/, '/recipe/:id/edit'],
  [/^\/recipe\/[^/]+$/, '/recipe/:id'],
  [/^\/user\/[^/]+$/, '/user/:id'],
];

export function routePattern(pathname) {
  const path = pathname || '/';
  for (const [re, pattern] of ROUTE_PATTERNS) {
    if (re.test(path)) return pattern;
  }
  return path;
}

// Firebase download URLs carry an access token; emails and long hex ids show
// up in thrown messages. None of it belongs in a log we grep casually.
export function scrubMessage(text) {
  return String(text || '')
    .replace(/[\w.+-]+@[\w-]+\.[\w.]+/g, '<email>')
    .replace(/token=[\w-]+/gi, 'token=<redacted>')
    .replace(/(key|apiKey|password|secret)=[^&\s]+/gi, '$1=<redacted>')
    .replace(/\/o\/[^?\s]+/g, '/o/<path>')
    .slice(0, MAX_MESSAGE);
}

// One line per distinct problem, so ten identical failures in a render loop
// are one report rather than ten.
function fingerprint(message, source) {
  return `${source}::${message.slice(0, 120)}`;
}

function shouldSend(fp) {
  if (sentCount >= MAX_PER_SESSION) return false;
  const now = Date.now();
  const last = recentlySent.get(fp);
  if (last && now - last < DEDUPE_WINDOW_MS) return false;
  recentlySent.set(fp, now);
  return true;
}

/**
 * Report an error. Never throws, never blocks, never awaits anything the
 * caller depends on -- reporting a failure must not itself become one.
 *
 * @param {Error|string} error
 * @param {{source?: string, context?: string}} options
 *   `context` is a short developer-supplied label ("loadRecipes"), never
 *   user input.
 */
export function reportError(error, { source = 'app', context = '' } = {}) {
  try {
    const message = scrubMessage(error?.message || error);
    if (!message) return;

    const fp = fingerprint(message, source);
    if (!shouldSend(fp)) return;
    sentCount += 1;

    const payload = {
      message,
      stack: scrubMessage(error?.stack || '').slice(0, MAX_STACK),
      source,
      context: String(context || '').slice(0, 120),
      route: routePattern(window.location?.pathname),
      userAgent: (navigator?.userAgent || '').slice(0, 300),
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      release: process.env.REACT_APP_VERSION || 'dev',
    };

    // Fire and forget. A rejected call is swallowed: if the transport is the
    // thing that's broken there is nothing useful left to do about it, and
    // an unhandled rejection here would re-enter the global handler below.
    httpsCallable(functions, 'logClientError')(payload).catch(() => {});
  } catch (_) {
    // Reporting must never be the reason a page breaks.
  }
}

/**
 * Install global handlers for the two error paths React cannot see:
 * uncaught exceptions outside render, and rejected promises nobody caught.
 * Called once from index.js. ErrorBoundary covers the render path.
 */
export function installGlobalErrorHandlers() {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (event) => {
    // Resource load failures (a broken <img>) also fire this event but carry
    // no error object; they're noise, not exceptions.
    if (!event.error) return;
    reportError(event.error, { source: 'window.onerror' });
  });

  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, { source: 'unhandledrejection' });
  });
}

// Test seam: the module keeps per-session counters, which would otherwise
// leak between test cases.
export function __resetForTests() {
  sentCount = 0;
  recentlySent.clear();
}
